import { useCallback, useMemo, useRef, useState } from 'react';
import { ArrowsClockwise, ArrowsCounterClockwise } from '@phosphor-icons/react';
import { useCampaignStabParam } from '../../../campaign-detail/useCampaignStabParam';
import { Sparkle as DsSparkle } from '../../../design-system/icons';
import {
  Activity,
  AlertTriangle,
  Check,
  CheckCircle2,
  ListChecks,
  Loader2,
  MessageSquare,
  Sparkles,
  Wand2,
  XCircle,
} from '../../../icons';
import type { AssignedProduct } from '@discovr/types';
import { Button } from '../../ui/button';
import { GeoAgentBanner } from '../GeoAgentBanner';
import { LinkedProductsBar } from '../LinkedProductsBar';
import {
  type ContentReviewBody,
  type ContentReviewResult,
  type GeoAiEnvelope,
  humanizeKey,
  passFailTone,
  severityRank,
  severityTone,
} from './types';
import { computeProductMatch, type ProductMatchVerdict } from './productMatch';

type TabKey =
  | 'overview'
  | 'issues'
  | 'pass_fail'
  | 'advanced'
  | 'fixes'
  | 'transcript'
  | 'geo';

// Tab list is fixed; per-tab visibility is decided at render time based on
// whether AI emitted the relevant fields. The 'geo' tab is only offered when
// `showGeo` is true (YouTube campaigns) — it's filtered out of the render
// below otherwise.
const TABS: ReadonlyArray<{ id: TabKey; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'issues', label: 'Issues' },
  { id: 'pass_fail', label: 'Pass/fail' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'fixes', label: 'Exact fixes' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'geo', label: 'GEO Optimized' },
];

export type ContentResultTabsProps = {
  /** Full AI body. `data.result` carries the LLM-normalised review fields. */
  data?: ContentReviewBody;
  /** AI status — drives whether the AI tabs render their content vs.
   * a "running…" / "failed" placeholder. */
  aiStatus?: 'pending' | 'complete' | 'failed';
  /** Optional error message shown when aiStatus === 'failed' (admin only). */
  aiError?: string;
  /**
   * When provided, each timestamped issue and exact-fix row exposes an
   * "Add to Comment" button that hands the formatted text up to the
   * embedding panel's chat draft. Mirrors ScriptResultTabs.
   */
  onAddComment?: (text: string) => void | Promise<void>;
  /**
   * Audience that's looking at this review. Brand sees a softened
   * pending banner when the AI run failed (so the creator doesn't see
   * raw error states); admin sees the actual "failed" banner with
   * error details so they can act. Defaults to 'admin'.
   */
  audience?: 'brand' | 'admin';
  /**
   * Roster entry id. No longer consumed inside this file — the GEO tab is
   * purely prop-fed (geoAnalysis/showGeo below) and makes no network calls of
   * its own. Kept (renamed from `entryId`) only because a caller may still
   * want it to build the `onRegenerateGeoFinalize` mutation it passes in.
   */
  geoEntryId?: string;
  /** GEO finalize envelope (ContentDetail.geo_finalize) — real chapter
   *  timestamps + a publish-ready description stitched onto the uploaded
   *  video's transcript. Drives the "GEO Optimized" tab; independent of
   *  aiStatus/data (the content-review AI review runs separately). Mirrors
   *  ScriptReviewPanel's `geoAnalysis` prop. */
  geoAnalysis?: GeoAiEnvelope;
  /** Show the "GEO Optimized" tab. Gate to YouTube campaigns at the call
   *  site — mirrors ScriptReviewPanel's `showGeo`. */
  showGeo?: boolean;
  /** Regenerate the whole GEO finalize pack (POST
   *  /roster/{entryId}/geo/finalize/regenerate) — wired by the panel.
   *  Hidden when `geoReadOnly` OR when this isn't provided at all (e.g. a
   *  brand viewer with no admin write permission) — matches
   *  ScriptResultTabs' `if (readOnly || !onRegenerate) return null;`, so the
   *  pill is simply absent rather than rendered-but-inert. May return a
   *  Promise — GeoFinalizeTab awaits it to drive a local busy/spinner state
   *  while the request is in flight (mirrors ScriptResultTabs' `regenBusy`). */
  onRegenerateGeoFinalize?: () => void | Promise<void>;
  /** Rephrase a single finalized element (POST /roster/{entryId}/geo/regenerate
   *  then re-finalize) — wired by the panel, same pattern as
   *  `onRegenerateGeoFinalize`. Renders the per-section "Rephrase" button only
   *  when this is provided AND `geoReadOnly` is false — the public/read-only
   *  surfaces simply don't pass it. */
  onRephraseGeoElement?: (element: 'chapters' | 'description') => void | Promise<void>;
  /** Save the FULL next finalize-selection array (POST
   *  /roster/{entryId}/geo/finalize/selection) — wired by the panel, same
   *  pattern as `onRegenerateGeoFinalize`. GeoFinalizeTab computes the next
   *  array itself (add/remove the toggled key from its own local `selected`
   *  state) and hands over the WHOLE array, not just the toggled key —
   *  mirrors ScriptResultTabs' `onSaveGeoSelection` contract, so this tab's
   *  local state is the single source of truth and the caller never keeps a
   *  second, independently-seeded copy that can drift out of sync with what
   *  the checkbox shows. Renders the interactive "Select" checkbox only when
   *  this is provided AND `geoReadOnly` is false; otherwise the static
   *  "Selected" badge is shown instead. */
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
  /** Read-only GEO surface (Confirmed/finalized tab): hides the manual
   *  Regenerate button. Mirrors ScriptReviewPanel's `geoReadOnly`. Defaults
   *  to false for live review callers. */
  geoReadOnly?: boolean;
  /** Products assigned to this creator (Multi-Product Campaigns) — drives the
   *  Overview product-match card (assigned vs AI detected_product). */
  assignedProducts?: AssignedProduct[];
  /** Campaign product-catalog size — gates the product-match card to >= 2. */
  campaignProductCount?: number;
};

export function ContentResultTabs({
  data,
  aiStatus,
  aiError,
  onAddComment,
  audience = 'admin',
  geoAnalysis,
  showGeo,
  onRegenerateGeoFinalize,
  onRephraseGeoElement,
  onSaveGeoSelection,
  geoReadOnly,
  assignedProducts,
  campaignProductCount,
}: ContentResultTabsProps) {
  const [urlStab, setStab] = useCampaignStabParam('overview');
  const validStab = (v: string): v is TabKey =>
    ['overview', 'issues', 'pass_fail', 'advanced', 'fixes', 'transcript', 'geo'].includes(v);
  const [activeTab, setActiveTabState] = useState<TabKey>(validStab(urlStab) ? urlStab : 'overview');
  const setActiveTab = useCallback((id: TabKey) => {
    setActiveTabState(id);
    setStab(id);
  }, [setStab]);

  // GEO tab only renders for YouTube campaigns (showGeo), same gate as
  // ScriptResultTabs.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => t.id !== 'geo' || !!showGeo),
    [showGeo],
  );

  // Roving tabindex (WAI-ARIA APG Tabs pattern) — same as ScriptResultTabs'
  // tablist; ported here since this tab bar had none of it (no role, no
  // aria-selected, no arrow-key nav), despite looking identical.
  const tabRefs = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});
  const focusTab = (id: TabKey) => {
    setActiveTab(id);
    tabRefs.current[id]?.focus();
  };
  const handleTablistKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = visibleTabs.findIndex((t) => t.id === activeTab);
    if (currentIndex === -1) return;
    let nextIndex: number;
    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % visibleTabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = visibleTabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextTab = visibleTabs[nextIndex];
    if (nextTab) focusTab(nextTab.id);
  };

  const result = data?.result;
  const aiReady = aiStatus === 'complete' && !!result;
  // For brand: soften "failed" into a pending-style banner so creators
  // don't see raw retry/failure messaging. Admin still sees the real
  // failed banner with the error message.
  const showAsFailed = aiStatus === 'failed' && audience === 'admin';
  const aiPending = aiStatus === 'pending' || (aiStatus === 'failed' && audience === 'brand');

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col">
      {/* Tabs + Link Products form one combined block (Figma 2890:55585/55564)
          — flush against each other, no gap.
          shrink-0, not sticky: ContentReviewPanel's scroll region now wraps
          ONLY the content below (see that file's comment) — the vertical
          scrollbar runs alongside the tab content, not the tab bar above
          it, and the tab bar needs no special positioning to stay put. */}
      <div className="flex w-full min-w-0 shrink-0 flex-col bg-card">
        {/* Tab navigation — Figma "content review" tabs (3072:75633): each tab is
            a column (label + a real 2px underline bar below it, not an absolute
            overlay), active = foreground-strong (#171717) text + underline,
            inactive = muted-foreground. gap-5 (tighter than Script Review's gap-8)
            keeps more tabs visible before the row needs to scroll — the video
            column is a fixed width now, so this row (not the column) absorbs any
            overflow via its own horizontal scroll rather than compressing video. */}
        <div
          role="tablist"
          aria-label="Content review sections"
          onKeyDown={handleTablistKeyDown}
          className="flex min-w-0 flex-nowrap items-center gap-5 overflow-x-auto border-b border-border px-5 pt-1"
        >
          {visibleTabs.map((tab) => (
            <Button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              type="button"
              variant="ghost"
              role="tab"
              id={`content-review-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`content-review-tabpanel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              className="flex h-auto min-w-0 shrink-0 flex-col items-center justify-end gap-1 rounded-none px-0.5 pb-px hover:bg-transparent"
            >
              <span
                className={
                  activeTab === tab.id
                    ? 'inline-flex items-center gap-1 text-[14px] font-medium text-foreground'
                    : 'inline-flex items-center gap-1 text-[14px] font-medium text-muted-foreground'
                }
              >
                {tab.label}
                {tab.id === 'geo' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#6C09F7] px-1.5 py-0.5 text-white">
                    <DsSparkle size={12} weight="regular" />
                    <span className="text-caption-regular">New</span>
                  </span>
                )}
              </span>
              <span
                className={
                  activeTab === tab.id
                    ? 'h-0.5 w-full rounded-[2px] bg-foreground'
                    : 'h-0.5 w-full rounded-[2px] bg-transparent'
                }
              />
            </Button>
          ))}
        </div>
        {/* Figma 2890:55564 — sits directly below the tab bar, above the tab
            content. Renders nothing below 2 campaign products / no assignment. */}
        <LinkedProductsBar products={assignedProducts} campaignProductCount={campaignProductCount} />
      </div>
      {/* Only this region scrolls — pb-5 lives here so it scrolls WITH the
          content, giving real breathing room at the true bottom instead of
          the bottom of a now-full-height, non-scrolling flex column. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-3 px-5 pt-3 pb-5">
        {aiPending ? (
          <StatusBanner
            tone="amber"
            icon={<Activity size={14} />}
            title="Analyzing the video"
            message="The AI review is being prepared — back in a minute or two."
          />
        ) : null}
        {showAsFailed ? (
          <StatusBanner
            tone="rose"
            icon={<XCircle size={14} />}
            title="AI review failed"
            message={aiError || 'Re-run the review to refresh.'}
          />
        ) : null}

        <div
          role="tabpanel"
          id={`content-review-tabpanel-${activeTab}`}
          aria-labelledby={`content-review-tab-${activeTab}`}
          tabIndex={0}
        >
        {/* GEO tab is purely prop-fed (geoAnalysis) — independent of the AI
            review status, same as ScriptResultTabs' GeoTab. No auto-fire, no
            network call from this component; only rendered while active.
            Gated on `showGeo` too, not just `activeTab === 'geo'` — the tab
            BUTTON is already hidden via `visibleTabs` when showGeo is false,
            but a stale `?stab=geo` URL (bookmark, back/forward, a campaign
            that used to be YouTube) can still seed `activeTab` to 'geo' with
            no visible tab left to click away from it. Gating the body the
            same way the button is gated closes that gap. */}
        {showGeo && activeTab === 'geo' ? (
          <GeoFinalizeTab
            geoAnalysis={geoAnalysis}
            readOnly={geoReadOnly}
            videoReviewReady={aiStatus === 'complete'}
            audience={audience}
            onRegenerateGeoFinalize={onRegenerateGeoFinalize}
            onRephraseGeoElement={onRephraseGeoElement}
            onSaveGeoSelection={onSaveGeoSelection}
          />
        ) : null}

        {/* Tab body */}
        {aiReady && result ? (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                body={data}
                result={result}
                assignedProducts={assignedProducts}
                campaignProductCount={campaignProductCount}
              />
            )}
            {activeTab === 'issues' && (
              <IssuesTab result={result} onAddComment={onAddComment} />
            )}
            {activeTab === 'pass_fail' && <PassFailTab result={result} />}
            {activeTab === 'advanced' && <AdvancedTab result={result} />}
            {activeTab === 'fixes' && (
              <FixesTab result={result} onAddComment={onAddComment} />
            )}
            {activeTab === 'transcript' && <TranscriptTab result={result} />}
          </>
        ) : null}
        </div>
        </div>
      </div>
    </div>
  );
}

// ── GEO finalize ───────────────────────────────────────────────────────────

type GeoChapterTitle = string | { text: string; translations?: Record<string, string> };

/**
 * The content-review `geo_finalize` envelope's `data` shape — real chapter
 * timestamps + a publish-ready description stitched onto the uploaded
 * video's transcript. `GeoAiEnvelope` (reused below for the outer envelope —
 * status/analysis_id/error/selected) types `data` as `GeoPackWrapper`
 * (Script Review's un-stitched pack shape), which doesn't match this one:
 * `description` is a plain string, and it carries
 * `description_publish`/`captions_vtt`/`source` that `GeoPackWrapper` doesn't
 * have. Kept as a narrow local type for that reason (per the plan's own
 * carve-out for fields genuinely unique to content's response shape).
 *
 * IMPORTANT — this DOES carry a `.result` wrapper, same as Script Review's
 * `GeoPackWrapper`. The AI service's `GET /script/geo/finalize/{analysis_id}`
 * (discovr-ai-service `app/services/script_review/router.py`) returns
 * `{analysis_id, result: {chapters, description, ...}, roster_entry_id,
 * computed_at}`, and the Go gateway passes that body through verbatim as
 * `geo_finalize.data` — so `geoAnalysis.data` is the WHOLE envelope, not the
 * pack itself. Reading `.chapters` directly off `geoAnalysis.data` (as this
 * file used to) is always `undefined` for every real pack; only the fixture
 * shapes that skipped the `.result` wrapper happened to work. See
 * `GeoFinalizeEnvelopeData` below, which layers `result` on top of this type
 * for the unwrap.
 */
type GeoFinalizeData = {
  chapters?: Array<{ timestamp: string; title: GeoChapterTitle; answers?: string }>;
  /** Clean description body (Q&A + #ad), NO chapter block — shown in the box. */
  description?: string;
  /** Description + inline chapter block, for the actual YouTube upload/export. */
  description_publish?: string;
  captions_vtt?: string;
  source?: string;
};

/** The actual wire shape of `geoAnalysis.data` for a complete pack — the pack
 *  fields nested under `.result`, plus envelope metadata alongside it. A
 *  bare `GeoFinalizeData` (no `.result`) is also tolerated below as a
 *  defensive fallback, in case any caller ever hands over an already-unwrapped
 *  pack — harmless either way since real payloads always have `.result`. */
type GeoFinalizeEnvelopeData = GeoFinalizeData & {
  result?: GeoFinalizeData;
  analysis_id?: string;
  roster_entry_id?: string;
  computed_at?: string;
};

function chapterTitleText(title: GeoChapterTitle | undefined): string {
  return typeof title === 'string' ? title : title?.text ?? '';
}

/**
 * GeoFinalizeTab — purely prop-fed display of the `geo_finalize` envelope
 * (real chapter timestamps + a publish-ready description stitched onto the
 * uploaded video's transcript). Makes NO network calls of its own: status is
 * read straight off `geoAnalysis.status`. Every user-triggered action
 * (whole-pack Regenerate, per-element Rephrase, per-element Select toggle)
 * calls a callback prop that may return a Promise — the panel owns the
 * actual mutation, and this tab awaits it to drive a local busy/spinner
 * state (mirrors ScriptResultTabs' `regenBusy` pattern), so a click always
 * gives visible feedback instead of looking like a dead button while the
 * async round-trip (including the BE's 202-but-silently-noop case) is in
 * flight. Per-element selection state seeded from `geoAnalysis.selected` is
 * the single source of truth: a toggle computes the FULL next array locally
 * and hands the WHOLE array to `onSaveGeoSelection`, never just the toggled
 * key — mirrors ScriptResultTabs' `onSaveSelection` contract, so the caller
 * never keeps a second, independently-seeded copy of the same set that can
 * drift out of sync with what the checkbox actually shows.
 */
function GeoFinalizeTab({
  geoAnalysis,
  readOnly,
  videoReviewReady = true,
  audience = 'admin',
  onRegenerateGeoFinalize,
  onRephraseGeoElement,
  onSaveGeoSelection,
}: {
  geoAnalysis?: GeoAiEnvelope;
  readOnly?: boolean;
  /** Whether the content's own AI review/video analysis has finished — the
   *  modern equivalent of the old client-side `videoReady` prop. Finalize
   *  needs the transcript from that review, so before it's ready we show a
   *  passive "waiting for the video review" message instead of a generic
   *  empty/loading state. Defaults to `true` for standalone callers. */
  videoReviewReady?: boolean;
  /** Audience gate for the error state, mirroring ContentResultTabs' own
   *  `aiPending`/`showAsFailed` pattern for the main AI-review section:
   *  admin sees the real error + a "Try again" retry action; brand sees the
   *  same soft "waiting for review" message as the pending case instead —
   *  failures are masked for brand. Defaults to 'admin'. */
  audience?: 'brand' | 'admin';
  onRegenerateGeoFinalize?: () => void | Promise<void>;
  onRephraseGeoElement?: (element: 'chapters' | 'description') => void | Promise<void>;
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
}) {
  const status = geoAnalysis?.status;
  const loading = status === 'pending' || status === 'processing';
  const error = status === 'failed' ? (geoAnalysis?.error ?? 'Failed to finalize the GEO pack.') : null;
  // The real wire body nests chapters/description under `.result` (see
  // GeoFinalizeEnvelopeData's doc comment) — unwrap it, falling back to the
  // raw envelope for the (unlikely) case a caller ever hands over an
  // already-unwrapped pack.
  const rawGeoData = geoAnalysis?.data as unknown as GeoFinalizeEnvelopeData | undefined;
  const result = status === 'complete' ? (rawGeoData?.result ?? rawGeoData) : undefined;
  // Local copy of the persisted selection, seeded from `geoAnalysis.selected`
  // — the single source of truth for what's checked. Every toggle below
  // updates this state AND ships the resulting FULL array to the caller, so
  // there is no second copy anywhere that could disagree with the checkbox.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(geoAnalysis?.selected ?? []),
  );

  // Busy state per action — mirrors ScriptResultTabs' `regenBusy`: a click
  // sets busy, awaits the (possibly async) callback via Promise.resolve
  // (works whether the caller returns void or a real Promise), then clears
  // it in `finally` so a rejected promise still recovers the button.
  const [regenBusy, setRegenBusy] = useState(false);
  const [rephraseBusy, setRephraseBusy] = useState<'chapters' | 'description' | null>(null);
  const [selectBusy, setSelectBusy] = useState(false);

  const doRegen = async () => {
    if (!onRegenerateGeoFinalize) return;
    setRegenBusy(true);
    try {
      await Promise.resolve(onRegenerateGeoFinalize());
    } finally {
      setRegenBusy(false);
    }
  };

  const doRephrase = async (element: 'chapters' | 'description') => {
    if (!onRephraseGeoElement) return;
    setRephraseBusy(element);
    try {
      await Promise.resolve(onRephraseGeoElement(element));
    } finally {
      setRephraseBusy(null);
    }
  };

  const toggleSel = async (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
    if (!onSaveGeoSelection) return;
    setSelectBusy(true);
    try {
      await Promise.resolve(onSaveGeoSelection(Array.from(next)));
    } finally {
      setSelectBusy(false);
    }
  };

  // Per-section Rephrase control — parity with Script Review (Figma
  // 2190:39166): purple ghost label + two-arrow swap icon AFTER the text.
  // Hidden when read-only OR the caller didn't wire the callback (covers the
  // public/no-permission paths automatically, no separate check needed).
  const rephraseBtn = (element: 'chapters' | 'description') => {
    if (readOnly || !onRephraseGeoElement) return null;
    const busy = rephraseBusy === element;
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => void doRephrase(element)}
        disabled={loading || rephraseBusy !== null}
        className="h-7 gap-1.5 px-2 text-xs font-medium text-[#6C09F7] hover:text-[#5A08D0]"
      >
        {busy ? 'Rephrasing…' : 'Rephrase'}
        <ArrowsClockwise size={15} className={busy ? 'animate-spin' : ''} />
      </Button>
    );
  };

  // Checkbox Select — matches the Script Review control (20px, 1.5px border,
  // radius-6; checked = brand purple + white check). Interactive only when
  // not read-only AND the caller wired `onSaveGeoSelection`; otherwise falls
  // back to the static "Selected" badge (Task 1's read-only display).
  const selBox = (key: string) => {
    const on = selected.has(key);
    if (!readOnly && onSaveGeoSelection) {
      return (
        <button
          type="button"
          role="checkbox"
          aria-checked={on}
          aria-busy={selectBusy}
          onClick={() => void toggleSel(key)}
          className="flex items-center gap-2"
        >
          <span
            className="flex size-5 shrink-0 items-center justify-center rounded-[6px] border-[1.5px]"
            style={{ borderColor: on ? '#6C09F7' : '#E5E5E5', backgroundColor: on ? '#6C09F7' : 'transparent' }}
          >
            {selectBusy ? (
              <Loader2 size={12} className="animate-spin text-white" />
            ) : on ? (
              <Check size={13} className="text-white" />
            ) : null}
          </span>
          <span className="text-body-sm-medium text-[#737373]">Select</span>
        </button>
      );
    }
    return on ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
        <CheckCircle2 size={12} /> Selected
      </span>
    ) : null;
  };

  return (
    <div className="flex flex-col gap-5">
      {/* GEO Agent banner (shared with Script Review). The pack-level action is a
          solid purple "Regenerate" pill that re-finalizes against the video.
          Absent (not merely disabled) whenever there's no callback to call —
          matches rephraseBtn/selBox's `!readOnly && <callback>` gate, so a
          brand viewer with readOnly=false but no write permission (no
          callback wired) never sees a clickable no-op pill. */}
      <GeoAgentBanner
        subtitle="Chapters, finalized from your uploaded video."
        action={
          !readOnly && onRegenerateGeoFinalize ? (
            <Button
              type="button"
              onClick={() => void doRegen()}
              disabled={loading || regenBusy}
              className="h-auto shrink-0 gap-2 rounded-full bg-[#6C09F7] px-[14px] py-2 text-body-md-medium text-white hover:bg-[#5A08D0] disabled:opacity-60"
            >
              <ArrowsCounterClockwise size={16} className={loading || regenBusy ? 'animate-spin' : ''} />
              {loading || regenBusy ? 'Finalizing…' : 'Regenerate'}
            </Button>
          ) : undefined
        }
      />

      {/* Auto-runs once the video review is done — before that, finalize would
          just 409 (no transcript yet), so we wait passively instead of
          showing a generic loading/empty state. Once ready, an in-flight
          finalize keeps the same soft message; a brand viewer sees it for a
          `failed` status too (error masked, see below).
          Gated on `!result` too — the GEO pack finalizes independently of the
          content AI review (a re-run review, a failed review, or a public
          grant that never carries `ai_analysis` at all can all leave
          `videoReviewReady` false while `geoAnalysis` is already complete).
          Without this, the "waiting for review" message rendered ABOVE the
          fully-loaded chapters below at the same time — confusing and wrong;
          once there's real GEO data to show, that takes priority. */}
      {!videoReviewReady && !result ? (
        <div className="inline-flex items-center gap-1.5 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Waiting for the video review to finish…
        </div>
      ) : (loading && !result) || (!!error && audience === 'brand') ? (
        <div className="inline-flex items-center gap-1.5 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Finalizing the GEO pack — this runs
          automatically once everything&apos;s ready…
        </div>
      ) : null}

      {/* Error state is audience-gated, mirroring ContentResultTabs'
          aiPending/showAsFailed split for the main AI-review section: admin
          gets the real banner + a "Try again" retry action (same callback and
          busy state as the top Regenerate pill); brand never sees the
          failure — the soft waiting message above covers that case. */}
      {videoReviewReady && error && audience === 'admin' ? (
        <div className="flex flex-col gap-2">
          <StatusBanner
            tone="rose"
            icon={<XCircle size={14} />}
            title="Couldn't finalize"
            message={error}
          />
          {!readOnly && onRegenerateGeoFinalize ? (
            <div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => void doRegen()}
                disabled={loading || regenBusy}
                className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-violet-600"
              >
                <Sparkles size={14} /> {loading || regenBusy ? 'Retrying…' : 'Try again'}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {result ? (
        <>
          {/* Chapters — real timestamps + question-shaped titles from the video. */}
          {result.chapters && result.chapters.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-3 py-2">
                <SectionHeading icon={<ListChecks size={12} />} title="Chapters" />
                <div className="flex items-center gap-3">
                  {rephraseBtn('chapters')}
                  {selBox('chapters')}
                </div>
              </div>
              <ul className="divide-y divide-slate-100">
                {result.chapters.map((ch, i) => (
                  <li key={i} className="flex items-start gap-3 px-3 py-2.5">
                    <span className="mt-0.5 shrink-0 font-mono text-xs text-slate-500">
                      {ch.timestamp}
                    </span>
                    <span className="text-sm text-slate-800">{chapterTitleText(ch.title)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Description is intentionally hidden in Content Review for now — the
              finalize response still carries `description`/`description_publish`,
              so re-adding the card is a self-contained change when needed. */}
        </>
      ) : null}

      {/* Checks `!result?.chapters?.length`, not just `!result` — a future
          wire-shape drift (e.g. an unexpected extra wrapper layer) leaves
          `result` truthy but `chapters` empty/undefined; without this the
          panel would render neither the chapter list above nor this empty
          state, i.e. a silent blank panel. Degrading to the visible empty
          state here is the safer failure mode. */}
      {!error && !loading && !result?.chapters?.length ? (
        <EmptyState label="Finalize the GEO pack to generate chapters from your uploaded video." />
      ) : null}
    </div>
  );
}

// ── Reusable bits ──────────────────────────────────────────────────────────

function StatusBanner({
  tone,
  icon,
  title,
  message,
}: {
  tone: 'slate' | 'amber' | 'rose';
  icon: React.ReactNode;
  title: string;
  message: string;
}) {
  const cls =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : tone === 'rose'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-border bg-muted text-foreground';
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-body-sm-regular ${cls}`}>
      <span className="mt-0.5">{icon}</span>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-caption-regular opacity-90">{message}</div>
      </div>
    </div>
  );
}

function SectionHeading({
  title,
}: {
  // `icon` is still accepted from callers but not rendered — Figma section
  // labels (3072:75640) are uppercase caption text, no leading glyph.
  icon?: React.ReactNode;
  title: string;
}) {
  return (
    <div className="text-caption-regular/4 font-medium uppercase text-muted-foreground">{title}</div>
  );
}

// ── Overview: Publish Score + Verdict (Figma 2143:90869 / 2143:90878) ────────

// Score tier drives the ring + number + word colour. Figma shows the green
// "Safe" case (score 85); amber/red mirror the risk tiers for lower scores.
function scoreTier(score: number): { color: string; label: string } {
  if (score >= 75) return { color: '#009966', label: 'Safe' };
  if (score >= 50) return { color: '#D97706', label: 'Moderate' };
  return { color: '#DC2626', label: 'High risk' };
}

// Verdict pill tone — Figma: saturated colour at 12% for the bg, saturated text.
function pillTone(kind: 'good' | 'warn' | 'bad'): { bg: string; color: string } {
  if (kind === 'good') return { bg: 'rgba(0,153,102,0.12)', color: '#009966' };
  if (kind === 'bad') return { bg: 'rgba(220,38,38,0.12)', color: '#EF4444' };
  return { bg: 'rgba(234,179,8,0.12)', color: '#D97706' };
}

function recKind(rec: string): 'good' | 'warn' | 'bad' {
  if (rec === 'Approved') return 'good';
  if (rec === 'Rejected') return 'bad';
  return 'warn';
}

function riskKind(risk: string): 'good' | 'warn' | 'bad' {
  if (risk === 'Low') return 'good';
  if (risk === 'High') return 'bad';
  return 'warn';
}

/** Publish Score card — ring gauge with the number + safety word tinted to the
 *  score tier (Figma 2143:90869): a fixed 155px card — shrink-0 alone isn't
 *  enough, since align-items:stretch (the flex-col fallback below `sm`) would
 *  otherwise stretch it full-width. Track #D9D9D9, uppercase label, 107px ring. */
function PublishScoreCard({ score }: { score: number }) {
  const s = Math.min(100, Math.max(0, Math.round(score)));
  const { color, label } = scoreTier(s);
  const size = 107;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - s / 100);
  return (
    <div className="flex w-[155px] shrink-0 flex-col items-center gap-3 rounded-lg border border-border bg-card p-6">
      <span className="text-caption-regular/4 font-medium uppercase text-muted-foreground">
        Publish Score
      </span>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="block" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#D9D9D9" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1)' }}
          />
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-heading-h4/9 font-medium" style={{ color }}>{s}</span>
          <span className="text-caption-regular/4 font-medium" style={{ color }}>{label}</span>
        </div>
      </div>
    </div>
  );
}

/** Verdict card — "Verdict" label + rec/risk pills on one row, feedback below
 *  (Figma 3072:75640/75650). */
function VerdictCard({ rec, risk, feedback }: { rec?: string; risk?: string; feedback?: string }) {
  const recTone = rec ? pillTone(recKind(rec)) : null;
  const riskTone = risk ? pillTone(riskKind(risk)) : null;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3 rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-caption-regular/4 font-medium uppercase text-muted-foreground">
          Verdict
        </span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {rec && recTone ? (
            <span
              className="inline-flex items-center rounded-full px-3 py-1.5 text-caption-regular/4 font-medium"
              style={{ backgroundColor: recTone.bg, color: recTone.color }}
            >
              {rec}
            </span>
          ) : null}
          {risk && riskTone ? (
            <span
              className="inline-flex items-center rounded-full px-3 py-1.5 text-caption-regular/4 font-medium"
              style={{ backgroundColor: riskTone.bg, color: riskTone.color }}
            >
              Risk: {risk}
            </span>
          ) : null}
        </div>
      </div>
      {feedback ? (
        <p className="text-body-sm-regular/5 font-normal text-foreground">{feedback}</p>
      ) : null}
    </div>
  );
}

// ProductMatchCard — Multi-Product Campaigns. Green when the AI-detected product
// matches one the creator was assigned, red on a mismatch. Renders nothing for
// the 'none' verdict (feature dormant), so 0-1 product campaigns are unchanged.
function ProductMatchCard({ verdict }: { verdict: ProductMatchVerdict }) {
  if (verdict.kind === 'none') return null;
  const isMatch = verdict.kind === 'match';
  // Semantic tokens (success/destructive) rather than raw emerald/rose — matches
  // the repo convention (ActionConfirmDialog etc.) so the card themes correctly.
  const cls = isMatch
    ? 'border-success/20 bg-success/10 text-success'
    : 'border-destructive/20 bg-destructive/10 text-destructive';
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-body-sm-regular ${cls}`}
      data-testid="product-match-card"
    >
      <span className="mt-0.5 shrink-0">
        {isMatch ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
      </span>
      <div className="min-w-0">
        <div className="font-semibold">
          {isMatch ? 'Assigned product featured' : 'Product mismatch — wrong product'}
        </div>
        <div className="text-caption-regular opacity-90">
          {isMatch
            ? `The video promotes “${verdict.product}”, matching the assignment.`
            : `The video promotes “${verdict.detected}”, which isn't an assigned product.`}
        </div>
      </div>
    </div>
  );
}

// ── Overview ───────────────────────────────────────────────────────────────

function OverviewTab({
  body: _body,
  result,
  assignedProducts,
  campaignProductCount,
}: {
  body?: ContentReviewBody;
  result: ContentReviewResult;
  assignedProducts?: AssignedProduct[];
  campaignProductCount?: number;
}) {
  const score = typeof result.publish_score === 'number' ? result.publish_score : null;
  const risk = result.risk_level;
  const rec = result.status_recommendation;
  const productMatch = computeProductMatch(
    assignedProducts,
    result.detected_product?.primary_product_promoted,
    campaignProductCount,
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Publish Score + Verdict — Figma 2143:90869/90878. items-start (not
          stretch) so the fixed-width Publish Score card never grows to match
          Verdict's height, in the row layout or the narrow flex-col fallback. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row">
        {score !== null ? <PublishScoreCard score={score} /> : null}
        <VerdictCard rec={rec} risk={risk} feedback={result.feedback} />
      </div>

      {/* Product match (Multi-Product Campaigns) — assigned vs AI-detected.
          Renders nothing when the feature is dormant (< 2 products / no
          assignment / no detected product). */}
      <ProductMatchCard verdict={productMatch} />

      {/* Manual review banner — bordered card, uppercase heading, reasons as a
          single line below (Figma 3072:75657). */}
      {result.human_in_loop?.requires_manual_review ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-6">
          <span className="text-caption-regular/4 font-medium uppercase text-[#5a07ce]">
            Manual review recommended
          </span>
          {result.human_in_loop.manual_review_reasons?.length ? (
            <span className="text-body-sm-regular/5 font-normal text-[#5a07ce]">
              {result.human_in_loop.manual_review_reasons.join(' · ')}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Strengths */}
      {result.strengths && result.strengths.length > 0 ? (
        <div className="rounded-lg border border-border bg-card p-6">
          <SectionHeading icon={<CheckCircle2 size={12} />} title="Strengths" />
          <ul className="mt-3 space-y-2 text-body-sm-regular text-foreground">
            {result.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1 inline-block size-1.5 shrink-0 rounded-full bg-emerald-500" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Risk flags — each flag is its own full pill, stacked (Figma 3072:75660),
          not wrapped chips. */}
      {result.risk_flags && result.risk_flags.length > 0 ? (
        <div className="rounded-lg border border-border bg-card p-6">
          <SectionHeading icon={<AlertTriangle size={12} />} title="Risk Flags" />
          <div className="mt-3 flex flex-col items-start gap-2">
            {result.risk_flags.map((flag, i) => (
              <span
                key={i}
                className="inline-flex w-fit items-center rounded-full bg-[rgba(220,38,38,0.12)] px-3 py-1.5 text-left text-[12px] font-medium text-[#dc2626]"
              >
                {flag}
              </span>
            ))}
          </div>
        </div>
      ) : null}

    </div>
  );
}

// ── Issues ─────────────────────────────────────────────────────────────────

function IssuesTab({
  result,
  onAddComment,
}: {
  result: ContentReviewResult;
  onAddComment?: (text: string) => void | Promise<void>;
}) {
  const issues = useMemo(() => {
    const arr = result.timestamped_issues ?? [];
    return [...arr].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
  }, [result.timestamped_issues]);

  if (!result.timestamped_issues) return <EmptyState label="No timestamped issues in the AI response." />;
  if (issues.length === 0) return <EmptyState label="AI found no issues to flag." />;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <table className="w-full table-fixed text-left text-body-sm-regular">
        <thead className="bg-stone-50 text-[12px] font-medium text-muted-foreground uppercase">
          <tr>
            <th className="w-28 px-3 py-2">Severity</th>
            <th className="w-20 px-3 py-2">Time</th>
            <th className="px-3 py-2">Issue</th>
            <th className="px-3 py-2">Recommended Fix</th>
            {onAddComment ? <th className="w-32 px-3 py-2 text-right" /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {issues.map((issue, i) => {
            const tone = severityTone(issue.severity);
            return (
              <tr key={i} className="align-top">
                <td className="p-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-caption-medium ${tone.bg} ${tone.text}`}
                  >
                    {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                  </span>
                </td>
                <td className="p-3 font-mono text-caption-regular text-muted-foreground">{issue.timestamp}</td>
                <td className="p-3 text-foreground">{issue.issue}</td>
                <td className="p-3 text-foreground">{issue.fix}</td>
                {onAddComment ? (
                  <td className="p-3 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        void onAddComment(`@${issue.timestamp} (${issue.severity}): ${issue.issue}\nSuggested fix: ${issue.fix}`)
                      }
                      className="h-auto px-2 py-1 text-[10px] font-semibold"
                    >
                      Add to comment
                    </Button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Pass / Fail + Automated Checks ─────────────────────────────────────────

function PassFailTab({ result }: { result: ContentReviewResult }) {
  const checklist = result.pass_fail_checklist;
  const checks = result.automated_checks;

  const checklistEntries = checklist ? Object.entries(checklist) : [];

  if (!checklist && (!checks || checks.length === 0)) {
    return <EmptyState label="No pass/fail data in the AI response." />;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Campaign Brief — Figma 1672:30970: uppercase group label above a card
          of criterion rows (name + notes left, Pass/Fail pill right). */}
      {checklistEntries.length > 0 ? (
        <div className="flex flex-col gap-3">
          <GroupLabel>Campaign Brief</GroupLabel>
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {checklistEntries.map(([key, entry]) => {
              const tone = passFailTone(entry.status);
              const passed = String(entry.status).toLowerCase() === 'pass';
              return (
                <div key={key} className="flex items-start justify-between gap-4 px-[22px] py-[15px]">
                  <div className="min-w-0">
                    <div className="text-body-sm-medium text-foreground">{humanizeKey(key)}</div>
                    {entry.notes ? (
                      <p className="mt-0.5 text-caption-medium text-muted-foreground">{entry.notes}</p>
                    ) : null}
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-caption-medium ${tone.bg} ${tone.text}`}
                  >
                    {passed ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                    {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {checks && checks.length > 0 ? (
        <div className="flex flex-col gap-3">
          <GroupLabel>Automated Checks</GroupLabel>
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {checks.map((c, i) => (
              <div key={i} className="px-[18px] py-3.5">
                <div className="flex items-start gap-2.5">
                  {c.passed ? (
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                  ) : (
                    <XCircle size={20} className="mt-0.5 shrink-0 text-red-500" />
                  )}
                  <div className="flex-1">
                    <div className="text-body-sm-medium text-foreground">{c.title}</div>
                    {c.details ? (
                      <div className="mt-1 text-caption-medium text-muted-foreground">{c.details}</div>
                    ) : null}
                    {c.reasoning || c.recommendation ? (
                      <div className="mt-1 text-caption-medium text-foreground-subtle">
                        {c.reasoning ? `Reasoning: ${c.reasoning}` : ''}
                        {c.reasoning && c.recommendation ? '  ·  ' : ''}
                        {c.recommendation ? `Recommendation: ${c.recommendation}` : ''}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-caption-medium uppercase text-foreground-subtle">
      {children}
    </div>
  );
}

// ── Advanced ───────────────────────────────────────────────────────────────

function AdvancedTab({ result }: { result: ContentReviewResult }) {
  const adv = result.advanced_analysis;
  if (!adv) return <EmptyState label="No advanced analysis in the AI response." />;

  // Render only the keys AI actually emitted; preserve a stable order.
  const ordered: Array<[string, string | undefined]> = [
    ['Hook Effectiveness', adv.hook_effectiveness],
    ['CTA Effectiveness', adv.cta_effectiveness],
    ['Pacing Notes', adv.pacing_notes],
    ['Audio Quality', adv.audio_quality],
    ['Visual Quality', adv.visual_quality],
    ['Thumbnail Recommendation', adv.thumbnail_recommendation],
  ];
  const present = ordered.filter(([, v]) => typeof v === 'string' && v.length > 0);
  if (present.length === 0) {
    return <EmptyState label="Advanced analysis section was empty." />;
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {present.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-border bg-card p-6">
          <div className="text-body-sm-regular text-muted-foreground">{label}</div>
          <p className="mt-2 text-body-sm-regular leading-relaxed text-foreground">{value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Exact Fixes ────────────────────────────────────────────────────────────

function FixesTab({
  result,
  onAddComment,
}: {
  result: ContentReviewResult;
  onAddComment?: (text: string) => void | Promise<void>;
}) {
  const fixes = result.exact_fixes;
  if (!fixes) return <EmptyState label="No exact fixes in the AI response." />;
  if (fixes.length === 0) return <EmptyState label="AI found no concrete rewrites to suggest." />;

  return (
    <div className="flex flex-col gap-3">
      {fixes.map((fx, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-border bg-card"
        >
          <div className="bg-rose-50 px-4 py-3">
            <div className="text-body-sm-medium text-rose-600">Original</div>
            <p className="mt-1 text-body-sm-regular text-rose-700">{fx.original}</p>
          </div>
          <div className="bg-emerald-50 px-4 py-3">
            <div className="text-body-sm-medium text-emerald-700">Replacement</div>
            <p className="mt-1 text-body-sm-regular text-emerald-800">{fx.replacement}</p>
          </div>
          {onAddComment ? (
            <div className="flex justify-end border-t border-border bg-muted px-3 py-2">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void onAddComment(`Suggested rewrite — replace:\n> ${fx.original}\nwith:\n> ${fx.replacement}`)
                }
                className="h-auto px-2 py-1 text-[10px] font-semibold"
              >
                <Wand2 size={11} /> Add to comment
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ── Transcript ─────────────────────────────────────────────────────────────

function TranscriptTab({ result }: { result: ContentReviewResult }) {
  if (!result.script_extraction) {
    return <EmptyState label="AI did not extract a transcript." />;
  }
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <SectionHeading icon={<MessageSquare size={12} />} title="Extracted Transcript" />
      <p className="mt-3 whitespace-pre-wrap text-body-sm-regular leading-relaxed text-foreground">
        {result.script_extraction}
      </p>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/50 px-4 py-8 text-center text-body-sm-regular text-muted-foreground">
      {label}
    </div>
  );
}
